import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations, LANGUAGES } from "./translations";
import { trackLanguageChange } from "../lib/analytics";

const LanguageContext = createContext(null);

// Read ?lang= from URL first, then localStorage, default to ES
const detectInitialLang = () => {
  if (typeof window === "undefined") return "es";
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("lang");
    if (fromUrl && LANGUAGES.some((l) => l.code === fromUrl)) return fromUrl;
    const stored = localStorage.getItem("rebel_lang");
    if (stored && LANGUAGES.some((l) => l.code === stored)) return stored;
  } catch {}
  return "es";
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(detectInitialLang);

  useEffect(() => {
    try {
      const prev = localStorage.getItem("rebel_lang");
      localStorage.setItem("rebel_lang", lang);
      document.documentElement.lang = lang;
      // Sync URL ?lang= without reload (history.replaceState for clean shareable links)
      const url = new URL(window.location.href);
      if (url.searchParams.get("lang") !== lang) {
        url.searchParams.set("lang", lang);
        window.history.replaceState({}, "", url.toString());
      }
      // GA4 — fire only on user-initiated change (not on initial mount)
      if (prev && prev !== lang) {
        try { trackLanguageChange(lang); } catch {}
      }
      // Lazy-load Noto Sans SC only when the user picks Chinese, so
      // ES/EN/SK/DE users never download ~200KB of CJK glyphs. The
      // <link> is injected once (idempotent) and cached by the browser
      // afterwards. Fallback stack in body CSS covers users with
      // native CJK fonts (macOS PingFang, Windows Microsoft YaHei).
      if (lang === "zh-CN" && !document.getElementById("noto-sans-sc-font")) {
        const link = document.createElement("link");
        link.id = "noto-sans-sc-font";
        link.rel = "stylesheet";
        link.href =
          "https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;600;700&display=swap";
        document.head.appendChild(link);
      }
    } catch {}
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      t: translations[lang] || translations.en || translations.es,
      languages: LANGUAGES,
    }),
    [lang]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};
